import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext
} from 'react';
import axios from 'axios';
import { usersApi, authApi, setToken, clearToken } from 'api';
import { LoadingScreen } from 'components';

axios.defaults.baseURL = process.env.REACT_APP_API_URL;

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [initialLoading, setInitialLoading] = useState(true);

  const [auth, setAuth] = useState({ token: null, user: null });

  const hasGoogleAdsConnected = Boolean(auth.user?.google_ads_customer_id);
  const hasMetaConnected = Boolean(auth.user?.meta_ad_account_id);
  const hasSomeAccountConnected = hasGoogleAdsConnected || hasMetaConnected;

  const logout = useCallback(() => {
    localStorage.clear();
    clearToken();
    setAuth({ token: null, user: null });
    window.location.href = '/login';
  }, []);

  const loginWithEmail = useCallback(async (email, password) => {
    try {
      const response = await authApi.login(email, password);
      let { access_token, user } = response.data;

      setToken(access_token);
      const userResponse = await usersApi.getCurrentUser();
      const userData = userResponse.data;
      user = { ...user, ...userData };
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      setAuth({ token: access_token, user });
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
      throw error;
    }
  }, []);

  const loginWithToken = useCallback(async (access_token, user = null) => {
    try {
      setToken(access_token);
  
      if (!user) {
        const userResponse = await usersApi.getCurrentUser();
        user = userResponse.data;
      }
  
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      setAuth({ token: access_token, user });
    } catch (error) {
      console.error('Error en login con token:', error);
      throw error;
    }
  }, []);

  const updateContextUser = useCallback((data) => {
    setAuth(prev => {
      const newUser = { ...prev.user, ...data };
      localStorage.setItem('user', JSON.stringify(newUser));
      return { ...prev, user: newUser };
    });
  }, []);

  const updateUser = useCallback(async (dataToUpdate) => {
    if (!dataToUpdate.id) return;
    try {
      const { data } = await usersApi.updateUser({ ...auth.user, ...dataToUpdate });
      updateContextUser(data);
      return data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw new Error('Error al actualizar el usuario: ' + (error.response?.data?.error || error.message));
    }
  }, [auth.user, updateContextUser]);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setInitialLoading(false);
      return;
    }
    setToken(token);

    const stored = localStorage.getItem('user');
    if (stored) {
      setAuth({ token, user: JSON.parse(stored) });
    }

    (async () => {
      try {
        const userRes = await usersApi.getCurrentUser();
        const freshUser = { ...userRes.data };
        setAuth({ token, user: freshUser });
        localStorage.setItem('user', JSON.stringify(freshUser));
      } catch (err) {
        console.error(err);
        logout();
      } finally {
        setInitialLoading(false);
      }
    })();
  }, [logout]);

  const value = {
    auth,
    loginWithEmail,
    loginWithToken,
    logout,
    updateUser,
    updateContextUser,
    hasGoogleAdsConnected,
    hasMetaConnected,
    hasSomeAccountConnected,
    initialLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {initialLoading ? <LoadingScreen /> : children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
